import { useMemo, useState } from 'react';
import { useApi } from '../lib/useApi.ts';
import { useCanWrite } from '../lib/auth.tsx';
import { post } from '../lib/api.ts';
import { duration, shortDate, timeOfDay } from '../lib/format.ts';
import { Badge, Button, Card, EmptyState, Skeleton, Tabs } from '../components/ui/index.ts';
import { AlertIcon, CheckIcon, UserIcon, VideoIcon, ClockIcon } from '../components/icons.tsx';
import { cx } from '../lib/cx.ts';

interface Invitee {
  name: string | null;
  email: string | null;
  is_external: boolean;
  response?: string | null;
}

interface Candidate {
  lead_id: number;
  full_name: string;
  score: number;
  why: string;
}

interface ReviewItem {
  id: number;
  source: 'fathom' | 'google_calendar';
  external_id: string;
  external_url: string | null;
  title: string | null;
  starts_at: string | null;
  duration_seconds: number | null;
  invitees: Invitee[] | null;
  candidates: Candidate[] | null;
  proposed_lead_id: number | null;
  proposed_lead_name: string | null;
  match_basis: string | null;
  confidence: 'confident' | 'ambiguous' | 'none';
  reason: string;
  status: string;
  suggested_call_type: string;
}

/**
 * Fathom never reports a meeting type, so this is always shown and always
 * confirmed rather than defaulted silently. Getting it wrong changes how the
 * analysis reads the call: a discovery and an assessment review are not the
 * same artifact.
 */
const CALL_TYPES: { value: string; label: string }[] = [
  { value: 'discovery', label: 'Discovery' },
  { value: 'assessment_review', label: 'Assessment review' },
  { value: 'crm_exploration', label: 'CRM exploration' },
  { value: 'kickoff', label: 'Kickoff' },
  { value: 'one_on_one', label: '1-on-1' },
  { value: 'renewal', label: 'Renewal' },
  { value: 'other', label: 'Other' },
];

interface Response {
  items: ReviewItem[];
  total: number;
  counts: { pending: string; resolved: string; dismissed: string; pending_confident: string };
}

/**
 * The approval gate. Nothing from Fathom or Google Calendar reaches the
 * database until it is approved here, so this page is the only way an
 * ingested call becomes real.
 */
export default function Review() {
  const [tab, setTab] = useState('pending');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  // id -> chosen call type, seeded from each item's suggestion.
  const [callTypes, setCallTypes] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const canWrite = useCanWrite();
  const { data, error, loading, reload } = useApi<Response>(`/review?status=${tab}&limit=200`);

  const confidentIds = useMemo(
    () =>
      (data?.items ?? [])
        .filter((item) => item.confidence === 'confident' && item.proposed_lead_id)
        .map((item) => item.id),
    [data],
  );

  function setCallType(id: number, value: string) {
    setCallTypes((current) => ({ ...current, [id]: value }));
  }

  function callTypeFor(item: ReviewItem): string {
    return callTypes[item.id] ?? item.suggested_call_type ?? 'discovery';
  }

  function toggle(id: number) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function approveSelected() {
    setBusy(true);
    setBanner(null);
    try {
      const chosen = (data?.items ?? []).filter((item) => selected.has(item.id));
      const result = await post<{ approved: unknown[]; failed: { id: number; error: string }[] }>(
        '/review/approve',
        { items: chosen.map((item) => ({ id: item.id, call_type: callTypeFor(item) })) },
      );
      setSelected(new Set());
      setBanner(
        result.failed.length === 0
          ? `Approved ${result.approved.length}.`
          : `Approved ${result.approved.length}, ${result.failed.length} failed: ${result.failed[0]?.error ?? ''}`,
      );
      reload();
    } catch (err: unknown) {
      setBanner(err instanceof Error ? err.message : 'Bulk approve failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-xl font-semibold tracking-tighter">Review</h1>
        <p className="mt-0.5 text-xs text-fg-subtle">
          Every recording and booking waits here. Nothing is created until you approve it.
        </p>
      </header>

      <Tabs
        items={[
          { id: 'pending', label: 'Pending', count: Number(data?.counts.pending ?? 0) },
          { id: 'resolved', label: 'Approved', count: Number(data?.counts.resolved ?? 0) },
          { id: 'dismissed', label: 'Dismissed', count: Number(data?.counts.dismissed ?? 0) },
        ]}
        value={tab}
        onChange={(next) => {
          setTab(next);
          setSelected(new Set());
        }}
      />

      {banner && <p className="text-xs text-fg-muted">{banner}</p>}
      {error && <p className="text-sm text-lost">{error}</p>}

      {tab === 'pending' && canWrite && confidentIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2.5">
          <span className="text-xs text-fg-muted">
            {selected.size > 0 ? `${selected.size} selected` : `${confidentIds.length} pre-matched`}
          </span>
          <div className="ml-auto flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() =>
                setSelected(
                  selected.size === confidentIds.length ? new Set() : new Set(confidentIds),
                )
              }
            >
              {selected.size === confidentIds.length ? 'Clear' : 'Select all pre-matched'}
            </Button>
            <Button
              size="sm"
              variant="primary"
              loading={busy}
              disabled={selected.size === 0}
              leading={<CheckIcon size={13} />}
              onClick={() => void approveSelected()}
            >
              Approve {selected.size > 0 ? selected.size : ''}
            </Button>
          </div>
        </div>
      )}

      {loading && (
        <div className="space-y-3">
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} className="h-32 w-full" />
          ))}
        </div>
      )}

      {!loading && data && data.items.length === 0 && (
        <Card>
          <EmptyState
            icon={<CheckIcon size={18} />}
            title={tab === 'pending' ? 'Nothing waiting on you' : `No ${tab} items`}
            description={
              tab === 'pending'
                ? 'Recordings and bookings appear here as they arrive. Run the ingest jobs to pull the latest.'
                : 'Items you decide on will appear here.'
            }
            compact
          />
        </Card>
      )}

      <div className="space-y-3">
        {!loading &&
          data?.items.map((item) => (
            <ReviewCard
              key={item.id}
              item={item}
              canWrite={canWrite}
              selected={selected.has(item.id)}
              callType={callTypeFor(item)}
              onCallType={(value) => setCallType(item.id, value)}
              onToggle={() => toggle(item.id)}
              onDone={reload}
            />
          ))}
      </div>
    </div>
  );
}

function ReviewCard({
  item,
  canWrite,
  selected,
  callType,
  onCallType,
  onToggle,
  onDone,
}: {
  item: ReviewItem;
  canWrite: boolean;
  selected: boolean;
  callType: string;
  onCallType: (value: string) => void;
  onToggle: () => void;
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState(
    item.invitees?.find((invitee) => invitee.is_external)?.name ?? item.title ?? '',
  );

  async function act(path: string, payload?: unknown) {
    setBusy(true);
    setError(null);
    try {
      await post(path, payload ?? {});
      onDone();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'That did not work.');
      setBusy(false);
    }
  }

  const external = (item.invitees ?? []).filter((invitee) => invitee.is_external);
  const pending = item.status === 'pending';
  const isCalendar = item.source === 'google_calendar';
  const preMatched = item.confidence === 'confident' && item.proposed_lead_id !== null;

  return (
    <Card>
      <div className="flex items-start gap-3">
        {pending && canWrite && preMatched && (
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggle}
            aria-label={`Select ${item.title ?? 'item'}`}
            className="mt-1 size-3.5 shrink-0 accent-accent"
          />
        )}

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-sm font-medium text-fg">{item.title ?? 'Untitled'}</h3>
                <Badge tone={isCalendar ? 'open' : 'neutral'}>
                  {isCalendar ? 'Booking' : 'Recording'}
                </Badge>
                {preMatched && <Badge tone="won" dot>Pre-matched</Badge>}
                {!pending && (
                  <Badge tone={item.status === 'resolved' ? 'won' : 'neutral'}>
                    {item.status === 'resolved' ? 'approved' : item.status}
                  </Badge>
                )}
              </div>
              <p className="numeric mt-0.5 flex items-center gap-1.5 text-xs text-fg-subtle">
                {isCalendar ? <ClockIcon size={12} /> : <VideoIcon size={12} />}
                {shortDate(item.starts_at)}
                {timeOfDay(item.starts_at) && `, ${timeOfDay(item.starts_at)}`}
                {item.duration_seconds !== null && ` · ${duration(item.duration_seconds)}`}
              </p>
            </div>
            {item.external_url && (
              <a
                href={item.external_url}
                target="_blank"
                rel="noreferrer"
                className="shrink-0 text-xs text-accent-text transition hover:underline"
              >
                {isCalendar ? 'Open in Calendar' : 'Open in Fathom'}
              </a>
            )}
          </div>

          <p
            className={cx(
              'mt-2.5 flex items-start gap-1.5 text-xs',
              preMatched ? 'text-fg-muted' : 'text-risk',
            )}
          >
            {preMatched ? (
              <CheckIcon size={13} className="mt-px shrink-0 text-won" />
            ) : (
              <AlertIcon size={13} className="mt-px shrink-0" />
            )}
            {item.reason}
          </p>

          {external.length > 0 && (
            <ul className="mt-2 space-y-0.5">
              {external.map((invitee, index) => (
                <li key={index} className="flex items-center gap-1.5 text-xs text-fg-muted">
                  <UserIcon size={12} className="text-fg-subtle" />
                  {invitee.name ?? 'Unnamed'}
                  {invitee.email && <span className="text-fg-subtle">{invitee.email}</span>}
                </li>
              ))}
            </ul>
          )}

          {pending && canWrite && (
            <div className="mt-3.5 border-t border-border-subtle pt-3">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className="text-2xs text-fg-subtle">Call type</span>
                <select
                  value={callType}
                  onChange={(event) => onCallType(event.target.value)}
                  aria-label="Call type"
                  className="h-7 rounded-md border border-border bg-surface px-2 text-xs text-fg focus:border-border-strong"
                >
                  {CALL_TYPES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {callType === item.suggested_call_type && (
                  <span className="text-2xs text-fg-faint">suggested, confirm or change</span>
                )}
              </div>
              {preMatched && (
                <div className="mb-2.5 flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    variant="primary"
                    loading={busy}
                    leading={<CheckIcon size={13} />}
                    onClick={() => void act(`/review/${item.id}/approve`, { call_type: callType })}
                  >
                    Approve as {item.proposed_lead_name}
                  </Button>
                  <span className="text-2xs text-fg-subtle">{item.match_basis}</span>
                </div>
              )}

              {(item.candidates?.length ?? 0) > 0 && (
                <>
                  <p className="text-2xs text-fg-subtle">
                    {preMatched ? 'Or pick another lead' : 'Nearest leads considered'}
                  </p>
                  <div className="mt-1.5 space-y-1.5">
                    {item.candidates?.map((candidate) => (
                      <div
                        key={candidate.lead_id}
                        className="flex items-center justify-between gap-3 rounded-md bg-surface-inset px-2.5 py-1.5"
                      >
                        <span className="min-w-0 truncate text-xs">
                          <span className="text-fg">{candidate.full_name}</span>
                          <span className="ml-2 text-fg-subtle">{candidate.why}</span>
                        </span>
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={busy}
                          onClick={() =>
                            void act(`/review/${item.id}/approve`, { lead_id: candidate.lead_id, call_type: callType })
                          }
                        >
                          This one
                        </Button>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <div className="mt-3 flex flex-wrap items-center gap-2">
                {creating ? (
                  <>
                    <input
                      value={newName}
                      onChange={(event) => setNewName(event.target.value)}
                      placeholder="Full name"
                      className="h-7 flex-1 rounded-md border border-border bg-surface px-2.5 text-sm text-fg placeholder:text-fg-faint focus:border-border-strong"
                    />
                    <Button
                      size="sm"
                      variant="primary"
                      loading={busy}
                      disabled={newName.trim() === ''}
                      onClick={() =>
                        void act(`/review/${item.id}/approve`, { full_name: newName.trim(), call_type: callType })
                      }
                    >
                      Create and link
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setCreating(false)}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setCreating(true)}
                      disabled={busy}
                    >
                      Create a new lead
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy}
                      onClick={() => void act(`/review/${item.id}/dismiss`)}
                    >
                      Not a prospect
                    </Button>
                  </>
                )}
              </div>

              {error && <p className="mt-2 text-xs text-lost">{error}</p>}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
